import { FunctionComponent, memo, useContext, useMemo } from "react";
import { IInteractionsTaggedChartData, IProps } from "./phrase-triggers-interactions-tagged-chart.types";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import {
    formatChartData,
    formatStackChartData,
    getDateTimeXAxisTickInterval,
    getTopPhraseTriggers,
} from "./phrase-triggers-interactions-tagged-chart.utilities";
import { SpinnerUntil } from "@edgetier/components";
import MaximumChartData from "components-for/charts/maximum-chart-data";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { dateTimeUpperXAxisTickFormatter } from "utilities-for/charts/date-time-upper-x-axis-tick-formatter";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import usePhraseTriggers from "hooks-for/phrase-triggers/use-phrase-triggers";
import { PHRASE_TRIGGER_GROUP } from "../../phrase-triggers.constants";
import PhraseTriggersPerDayTooltip from "components-for/phrase-triggers/interactions-tagged-chart/phrase-triggers-per-day-tooltip";
import PhraseTriggersPerDayLegend from "components-for/phrase-triggers/interactions-tagged-chart/phrase-triggers-per-day-legend";
import PhraseTriggersPerTriggerTooltip from "components-for/phrase-triggers/interactions-tagged-chart/phrase-triggers-per-trigger-tooltip";
import {
    COLOURS_CHART,
    COLOURS_CHART_OTHERS,
    TOP_TRIGGERS_PER_DAY,
    TOP_TRIGGERS_PER_TRIGGER,
} from "./phrase-triggers-interactions-tagged-chart.constants";
import { PhraseTriggerContext } from "components-for/phrase-triggers/phrase-trigger-context/phrase-trigger-context";
import { Tooltip as TextTooltip } from "@edgetier/components";
import "./phrase-triggers-interactions-tagged-chart.scss";

/**
 * Chart component for displaying number of interactions tagged per day and
 * stacked chart for top five phrase triggers with number of interactions
 * @param props.tagId  Current tag Id passed
 * @param props.height Optional height of the chart
 * @param props.width  Optional width of the chart

 */
const PhraseTriggersInteractionsTaggedChart: FunctionComponent<IProps> = ({
    height = "100%",
    width = "100%",
    tagId,
}) => {
    const { filters } = useContext(PhraseTriggerContext);
    const { startDateTime, endDateTime, groupBy, dayTriggerView } = filters;
    const { data: phraseTriggers } = usePhraseTriggers({ tagId });

    // Get counts of phrases or interactions according to the parameters.
    const { data: filteredData, isLoading: isFilteredDataLoading } = useInteractionsCounts({
        startDateTime,
        endDateTime,
        tagId: [tagId],
        groupBy: [groupBy, PHRASE_TRIGGER_GROUP],
    });

    // Format chart data for either day or trigger filters
    const chartData: IInteractionsTaggedChartData[] = useMemo(
        () => (!dayTriggerView ? formatStackChartData(filteredData) : formatChartData(filteredData)),
        [dayTriggerView, filteredData]
    );
    // Array of triggers sorted by total interaction in the filter date range
    const topTriggers = useMemo(() => getTopPhraseTriggers(filteredData), [filteredData]);

    // Get top 5 phrase trigger keys for day view and top 10 trigger keys for trigger view
    const topPhraseTriggersKeys = topTriggers
        .slice(0, !dayTriggerView ? TOP_TRIGGERS_PER_DAY : TOP_TRIGGERS_PER_TRIGGER)
        .map(([id]) => id);

    const phraseTriggerNames: Record<string, string> = (phraseTriggers ?? []).reduce((previousData, phraseTrigger) => {
        return {
            ...previousData,
            ...(topPhraseTriggersKeys.includes(phraseTrigger.phraseTriggerId)
                ? {
                      [phraseTrigger.phraseTriggerId]: phraseTrigger.phrase,
                  }
                : {}),
        };
    }, {});

    // Total number of interactions from triggers
    const totalTriggerInteractions = useMemo(
        () => topTriggers.reduce((sum, [, value]) => sum + value, 0),
        [topTriggers]
    );

    return (
        <div className="phrase-triggers-interactions-tagged-chart">
            <SpinnerUntil<IInteractionsTaggedChartData[]> data={chartData} isReady={!isFilteredDataLoading}>
                {(data) => (
                    <MaximumChartData dataLength={data.length}>
                        <ResponsiveContainer width={width} height={height}>
                            <BarChart data={data}>
                                <CartesianGrid />
                                <YAxis yAxisId={0} tickFormatter={(value) => value} />

                                {dayTriggerView && (
                                    <>
                                        <XAxis
                                            dataKey="group"
                                            axisLine={false}
                                            scale="band"
                                            interval={0}
                                            tick={({ x, y, payload }) => {
                                                return (
                                                    <g transform={`translate(${x},${y})`}>
                                                        <text textAnchor="middle" fill="#666">
                                                            <TextTooltip
                                                                content={phraseTriggerNames[payload.value]}
                                                                delayEnter={250}
                                                                useArrow
                                                                placement="bottom-center"
                                                            >
                                                                <tspan x={0} y={0} dy={10}>
                                                                    {phraseTriggerNames[payload.value].substring(0, 10)}
                                                                    &hellip;
                                                                </tspan>
                                                            </TextTooltip>
                                                        </text>
                                                    </g>
                                                );
                                            }}
                                        />
                                        <Bar
                                            dataKey="count"
                                            radius={5}
                                            fill={"#3498db"}
                                            name="Number of interactions"
                                        />
                                        <Tooltip
                                            content={({ active, payload }) =>
                                                active &&
                                                typeof payload !== "undefined" &&
                                                Array.isArray(payload) &&
                                                payload.length > 0 && (
                                                    <PhraseTriggersPerTriggerTooltip
                                                        startDateTime={startDateTime!}
                                                        endDateTime={endDateTime!}
                                                        totalTriggerInteractions={totalTriggerInteractions}
                                                        tooltipData={payload[0]}
                                                        phraseTriggerNames={phraseTriggerNames}
                                                    />
                                                )
                                            }
                                            isAnimationActive={false}
                                        />
                                    </>
                                )}

                                {!dayTriggerView && (
                                    <>
                                        <XAxis
                                            dataKey="dateTime"
                                            axisLine={false}
                                            scale="band"
                                            interval={getDateTimeXAxisTickInterval(startDateTime!, endDateTime!)}
                                            tickFormatter={(tickValue) => {
                                                return dateTimeUpperXAxisTickFormatter(
                                                    startDateTime!,
                                                    tickValue,
                                                    TimePeriodGroupBy.Days
                                                );
                                            }}
                                        />
                                        <Bar
                                            dataKey="others"
                                            name="Others"
                                            stackId="topTriggers"
                                            fill={COLOURS_CHART_OTHERS}
                                            radius={[0, 0, 5, 5]}
                                        />
                                        {topPhraseTriggersKeys.reverse().map((triggerKey, index) => (
                                            <Bar
                                                dataKey={triggerKey}
                                                stackId="topTriggers"
                                                radius={index === topPhraseTriggersKeys.length - 1 ? [5, 5, 0, 0] : 0}
                                                fill={COLOURS_CHART[index]}
                                                key={triggerKey}
                                                style={{ strokeWidth: 0 }}
                                                name={phraseTriggerNames[triggerKey]}
                                            />
                                        ))}
                                        <Tooltip
                                            content={({ active, payload }) =>
                                                active &&
                                                Array.isArray(payload) &&
                                                payload.length > 0 && (
                                                    <PhraseTriggersPerDayTooltip tooltipData={payload} />
                                                )
                                            }
                                            isAnimationActive={false}
                                        />
                                        <Legend
                                            wrapperStyle={{ bottom: 0 }}
                                            content={({ payload }) =>
                                                typeof payload !== "undefined" &&
                                                Array.isArray(payload) &&
                                                payload.length > 0 && (
                                                    <PhraseTriggersPerDayLegend legendData={payload.reverse()} />
                                                )
                                            }
                                        />
                                    </>
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </MaximumChartData>
                )}
            </SpinnerUntil>
        </div>
    );
};

export default memo(PhraseTriggersInteractionsTaggedChart);
